import React, { useEffect, useState } from "react";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  User,
  GraduationCap,
  UserCheck,
  UserCog,
  ShieldCheck,
  FolderOpen,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Loader2,
  Shield,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import getBITSEndApiMap from "../apiMap";
import { tokenService } from "../services/TokenService";
import { ROLES } from "../constant/roles";

// Updated interface to accommodate project data from API
interface ProjectData {
  id: string;
  title?: string;
  name?: string;
  projectCode?: string;
  status: string;
  grade?: string;
  submissionDate?: string;
  submittedAt?: string;
  dueDate?: string;
  progress?: number;
  courseId?: string;
  course?: any;
}

interface UserProfileData {
  // Core fields
  id?: string;
  studentId?: string;
  userId?: string;
  name: string;
  email: string;
  role: string;
  roles?: string[]; // Multiple roles
  status?: string;
  lastLogin?: string;
  profilePicture?: string;
  rawUserId?: string; // The actual user ID for API calls

  // Contact & Personal
  phone?: string;
  address?: string;
  dateOfBirth?: string;
  startDate?: string;
  enrollmentDate?: string;

  // Academic (for students)
  currentGPA?: string | number;
  gpa?: string | number;
  totalCredits?: number;
  academicYear?: string;
  year?: string;
  department?: string;

  // Professional (for instructors/TAs)
  specialization?: string;
  experience?: string;

  // All projects for the user
  projects?: ProjectData[];

  // Course associations with projects
  courses?: Array<{
    courseId?: string;
    name?: string;
    courseName?: string;
    code?: string;
    courseCode?: string;
    instructor: string;
    enrolledOn?: string;
    enrollmentDate?: string;
    currentGrade?: string;
    status?: string;
    role?: string;
    projects?: ProjectData[];
  }>;
}

interface UserProfileModalProps {
  user?: any;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  fromPlatformGrade?: boolean;
  onClose?: () => void;
}

export function UserProfileModal({
  user,
  trigger,
  open,
  onOpenChange,
  fromPlatformGrade = false,
}: UserProfileModalProps) {
  const [detailedUser, setDetailedUser] = useState<any>(null);

  // Fetch detailed user data from API
  const { data: userDetailData, isLoading: isLoadingUserDetail } = useQuery({
    queryKey: ["userDetail", user?.rawUserId || user?.id],
    queryFn: async () => {
      const userId = user?.rawUserId || user?.id;
      if (!userId) return null;

      const token = tokenService.getToken();
      const apiUrl = getBITSEndApiMap("getUserById").replace(":id", userId);

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return response.data;
    },
    enabled: !!(user?.rawUserId || user?.id) && open !== false,
    staleTime: 5 * 60 * 1000,
  });

  // Update detailed user when API data arrives
  useEffect(() => {
    if (userDetailData?.isSuccess && userDetailData?.data) {
      const apiUser = userDetailData.data;

      // Extract all unique roles from courseRoles
      const allRoles = new Set<string>();
      apiUser.enrollments?.forEach((enrollment: any) => {
        if (enrollment.roleInCourse) {
          allRoles.add(enrollment.roleInCourse);
        }
      });

      // Transform API response to match our interface
      const transformedUser = {
        ...user,
        name: apiUser.fullname || user?.name,
        userId: apiUser.lmsId || user?.userId,
        studentId: apiUser.lmsId || user?.studentId,
        status: apiUser.status || user?.status,
        email: apiUser.email || user?.email,
        lastLogin: apiUser.lastLogin || user?.lastLogin,
        profilePicture: apiUser.profilePicture || user?.profilePicture,
        role: apiUser.roles?.[0]?.name || apiUser.role?.[0] || user?.role,
        roles:
          allRoles.size > 0
            ? Array.from(allRoles)
            : user?.roles || [user?.role],

        // Transform enrollments to courses
        courses:
          apiUser.enrollments?.map((enrollment: any) => ({
            courseId: enrollment.course?.id,
            name: enrollment.course?.name,
            courseName: enrollment.course?.name,
            code: enrollment.course?.code,
            courseCode: enrollment.course?.code,
            instructor: "Not specified",
            enrolledOn: enrollment.enrolledAt,
            enrollmentDate: enrollment.enrolledAt,
            status: enrollment.enrollmentStatus,
            role: enrollment.roleInCourse,
            // Filter projects for this course
            projects:
              apiUser.projects
                ?.filter(
                  (proj: any) => proj.course?.id === enrollment.course?.id
                )
                .map((proj: any) => ({
                  id: proj.id,
                  title: proj.title,
                  name: proj.title,
                  status: proj.status,
                  dueDate: proj.dueDate,
                  projectCode: proj.projectCode,
                  courseId: proj.course?.id,
                })) || [],
          })) ||
          user?.courses ||
          [],

        // All projects
        projects:
          apiUser.projects?.map((proj: any) => ({
            id: proj.id,
            title: proj.title,
            name: proj.title,
            status: proj.status,
            dueDate: proj.dueDate,
            projectCode: proj.projectCode,
            courseId: proj.course?.id,
            course: proj.course,
          })) ||
          user?.projects ||
          [],
      };

      setDetailedUser(transformedUser);
    } else {
      setDetailedUser(user);
    }
  }, [userDetailData, user]);

  const displayUser = detailedUser || user;

  if (!displayUser) {
    return null;
  }

  const getRoleIcon = (role: string) => {
    switch (role?.toLowerCase()) {
      case ROLES.STUDENT.toLowerCase():
        return <GraduationCap className="h-4 w-4" />;
      case ROLES.TA.toLowerCase():
        return <Shield className="h-4 w-4" />;
      case ROLES.INSTRUCTOR.toLowerCase():
        return <UserCog className="h-4 w-4" />;
      case ROLES.ADMIN.toLowerCase():
        return <ShieldCheck className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role?.toLowerCase()) {
      case ROLES.STUDENT.toLowerCase():
        return "bg-blue-50 text-blue-600";
      case ROLES.TA.toLowerCase():
        return "bg-purple-50 text-purple-600";
      case ROLES.INSTRUCTOR.toLowerCase():
        return "bg-blue-50 text-blue-700";
      case ROLES.ADMIN.toLowerCase():
        return "bg-red-50 text-red-600";
      default:
        return "bg-gray-50 text-gray-600";
    }
  };

  const getProjectStatusIcon = (status: string) => {
    const lowerStatus = status?.toLowerCase();
    if (
      lowerStatus === "completed" ||
      lowerStatus === "submitted" ||
      lowerStatus === "published"
    ) {
      return <CheckCircle className="h-4 w-4 text-bits-gold" />;
    } else if (lowerStatus === "in progress" || lowerStatus === "in_progress") {
      return <Clock className="h-4 w-4 text-bits-blue" />;
    } else if (lowerStatus === "pending") {
      return <AlertCircle className="h-4 w-4 text-bits-gold" />;
    } else {
      return <FileText className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getProjectStatusColor = (status: string) => {
    const lowerStatus = status?.toLowerCase();
    if (lowerStatus === "completed" || lowerStatus === "published") {
      return "bg-bits-completed text-bits-green-shade border-none";
    } else if (lowerStatus === "submitted") {
      return "bg-bits-gold/10 text-bits-gold border-bits-gold";
    } else if (lowerStatus === "in progress" || lowerStatus === "in_progress") {
      return "bg-bits-blue/10 text-bits-blue border-bits-blue";
    } else if (lowerStatus === "pending") {
      return "bg-bits-gold/10 text-bits-gold border-bits-gold";
    } else if (lowerStatus === "draft") {
      return "bg-gray-100 text-gray-600 border-gray-300";
    } else if (lowerStatus === "not started" || lowerStatus === "not_started") {
      return "bg-muted text-muted-foreground border-border";
    } else if (lowerStatus === "overdue") {
      return "bg-bits-overdue text-bits-strong-red border-none";
    } else {
      return "bg-muted text-muted-foreground border-border";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "Not specified"
      : date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        });
  };

  const getDisplayGPA = () => {
    const gpa = displayUser.currentGPA || displayUser.gpa;
    if (gpa === undefined || gpa === null || gpa === "N/A") return "N/A";
    const numericGPA = typeof gpa === "string" ? parseFloat(gpa) : gpa;
    return isNaN(numericGPA) ? "N/A" : numericGPA.toFixed(1);
  };

  const getDisplayYear = () => {
    return displayUser.academicYear || displayUser.year || "N/A";
  };

  const getDisplayUserId = () => {
    return displayUser.studentId || displayUser.userId || "N/A";
  };

  const getDisplayStartDate = () => {
    return displayUser.startDate || displayUser.enrollmentDate || "";
  };

  const formatStatus = (status: string) => {
    // Convert status to title case for display
    return status
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Get projects for a specific course
  const getProjectsForCourse = (course: any): ProjectData[] => {
    // First try to get projects from the course object itself
    if (course.projects && course.projects.length > 0) {
      return course.projects;
    }

    // If not, filter from all user projects by courseId
    if (
      displayUser.projects &&
      displayUser.projects.length > 0 &&
      course.courseId
    ) {
      return displayUser.projects.filter(
        (project: any) =>
          project.courseId === course.courseId ||
          project.course?.id === course.courseId
      );
    }

    return [];
  };

  // Get all unique roles to display
  const getAllRoles = () => {
    if (
      displayUser.roles &&
      Array.isArray(displayUser.roles) &&
      displayUser.roles.length > 0
    ) {
      return displayUser.roles;
    }
    return [displayUser.role];
  };

  // Handle controlled vs uncontrolled dialog
  const dialogProps = trigger
    ? {}
    : { open: open ?? false, onOpenChange: onOpenChange || (() => {}) };

  return (
    <Dialog {...dialogProps}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto bg-white border">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <User className="h-5 w-5 text-blue-600" />
            <span>User Profile</span>
          </DialogTitle>
          <DialogDescription>
            Complete profile information for{" "}
            {displayUser.name || "Unknown User"}.
          </DialogDescription>
        </DialogHeader>

        {isLoadingUserDetail ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-2">
              <Loader2 className="h-8 w-8 animate-spin text-bits-blue mx-auto" />
              <p className="text-muted-foreground">Loading user details...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Basic Information Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Full Name
                </Label>
                <p className="text-sm text-gray-900">
                  {displayUser.name || "Not specified"}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Role{getAllRoles().length > 1 ? "s" : ""}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {getAllRoles().map((role: string, index: number) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className={getRoleBadgeColor(role)}
                    >
                      {getRoleIcon(role)}
                      <span className="ml-1 capitalize">
                        {role || "Unknown"}
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  User ID
                </Label>
                <p className="text-sm text-gray-900">{getDisplayUserId()}</p>
              </div>
              {displayUser.status && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Status
                  </Label>
                  <Badge
                    variant="secondary"
                    className="text-green-600 bg-green-50 capitalize"
                  >
                    {displayUser.status}
                  </Badge>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Email
                </Label>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <p className="text-sm text-blue-600">
                    {displayUser.email || "Not specified"}
                  </p>
                </div>
              </div>

              {!fromPlatformGrade && displayUser.phone && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Phone
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <p className="text-sm text-gray-900">{displayUser.phone}</p>
                  </div>
                </div>
              )}

              {!fromPlatformGrade && getDisplayStartDate() && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Start Date
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <p className="text-sm text-gray-900">
                      {formatDate(getDisplayStartDate())}
                    </p>
                  </div>
                </div>
              )}

              {!fromPlatformGrade && displayUser.lastLogin ? (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Last Login
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <p className="text-sm text-gray-900">
                      {formatDate(displayUser.lastLogin)}
                    </p>
                  </div>
                </div>
              ) : fromPlatformGrade ? (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Department
                  </Label>
                  <p className="text-sm text-gray-900">
                    {displayUser.department || "Not specified"}
                  </p>
                </div>
              ) : null}
            </div>

            {/* Professional Information Section (for instructors/TAs) */}
            {(displayUser.specialization || displayUser.experience) && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {displayUser.specialization && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Specialization
                      </Label>
                      <p className="text-sm text-gray-900">
                        {displayUser.specialization}
                      </p>
                    </div>
                  )}
                  {displayUser.experience && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Experience
                      </Label>
                      <p className="text-sm text-gray-900">
                        {displayUser.experience}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Course Association Section */}
            {(displayUser.role?.toLowerCase() === ROLES.STUDENT.toLowerCase() ||
              displayUser.role?.toLowerCase() ===
                ROLES.INSTRUCTOR.toLowerCase() ||
              displayUser.role?.toLowerCase() === ROLES.TA.toLowerCase() ||
              displayUser.courses?.length) && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {fromPlatformGrade
                    ? "Project Details"
                    : "Course & Project Association"}
                </h3>

                {/* Course Details with Real Projects */}
                {displayUser.courses && displayUser.courses.length > 0 ? (
                  <div
                    className="space-y-4"
                    style={{ backgroundColor: "#F9FAFB" }}
                  >
                    {displayUser.courses.map((course: any, index: number) => {
                      const courseProjects = getProjectsForCourse(course);

                      return (
                        <div
                          key={index}
                          className="rounded-lg p-4"
                          style={{ border: "1px solid #E4E7EC" }}
                        >
                          {/* Course Header */}
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-900">
                              {course.name ||
                                course.courseName ||
                                "Unknown Course"}
                            </h4>
                            {!fromPlatformGrade &&
                              (course.code || course.courseCode) && (
                                <Badge variant="outline" className="text-xs">
                                  {course.code || course.courseCode}
                                </Badge>
                              )}
                          </div>

                          {/* Course Details */}
                          {!fromPlatformGrade && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600 mb-3">
                              <div>
                                <span className="font-semibold text-black">
                                  Role:
                                </span>{" "}
                                <span className="capitalize">
                                  {course.role || "Not specified"}
                                </span>
                              </div>
                              <div>
                                {/* <span className="font-medium">
                                  Enrolled On:
                                </span>{" "}
                                {formatDate(
                                  course.enrolledOn || course.enrollmentDate
                                )} */}
                              </div>
                            </div>
                          )}

                          {fromPlatformGrade && course.currentGrade && (
                            <div className="mb-3">
                              <h1 className="font-bold text-2xl">
                                {course.currentGrade}
                                <span className="text-lg font-medium text-bits-grey-600">
                                  {" "}
                                  Grade
                                </span>
                              </h1>
                            </div>
                          )}

                          {/* Projects Section - Show Real Data */}
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <div className="flex items-center space-x-2 mb-3">
                              <h5 className="font-medium text-gray-900">
                                Projects
                              </h5>
                              <span className="font-medium text-gray-600">
                                ({courseProjects.length})
                              </span>
                            </div>

                            {courseProjects.length > 0 ? (
                              <div className="space-y-2">
                                {courseProjects.map((project) => (
                                  <div
                                    key={project.id}
                                    className="bg-white rounded border p-3"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center space-x-2">
                                        {getProjectStatusIcon(project.status)}
                                        <span className="font-medium text-sm">
                                          {project.title ||
                                            project.name ||
                                            "Untitled Project"}
                                        </span>
                                      </div>
                                      <Badge
                                        variant="outline"
                                        className={getProjectStatusColor(
                                          project.status
                                        )}
                                      >
                                        {formatStatus(project.status)}
                                      </Badge>
                                    </div>

                                    <div className="flex items-center justify-between text-xs text-gray-600">
                                      <div className="flex items-center space-x-4">
                                        {project.grade && (
                                          <span>
                                            <span className="font-medium">
                                              Grade:
                                            </span>{" "}
                                            <span
                                              className={
                                                project.grade
                                                  .toString()
                                                  .startsWith("A") ||
                                                parseFloat(project.grade) >= 90
                                                  ? "text-green-600 font-medium"
                                                  : project.grade
                                                      .toString()
                                                      .startsWith("B") ||
                                                    parseFloat(project.grade) >=
                                                      80
                                                  ? "text-blue-600 font-medium"
                                                  : "text-yellow-600 font-medium"
                                              }
                                            >
                                              {project.grade}
                                            </span>
                                          </span>
                                        )}
                                        {(project.submissionDate ||
                                          project.submittedAt) && (
                                          <span>
                                            <span className="font-medium">
                                              Submitted:
                                            </span>{" "}
                                            {formatDate(
                                              project.submissionDate ||
                                                project.submittedAt
                                            )}
                                          </span>
                                        )}
                                        {project.dueDate && (
                                          <span className="flex gap-1 items-center">
                                            <Calendar className="w-4 h-4" />
                                            {formatDate(project.dueDate)}
                                          </span>
                                        )}
                                      </div>

                                      {project.progress !== undefined && (
                                        <div className="flex items-center space-x-2">
                                          <span className="text-xs">
                                            {project.progress}%
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-sm text-gray-500">
                                No projects assigned yet
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No course information available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default UserProfileModal;
